/**
 * V165.2 — Local SEO Business Page (Phase 3 Agent d)
 *
 * Renders /seo/{lang}/{city}/{category} programmatic landing pages targeting
 * hyper-local long-tail keywords (e.g., "SEO audit pre reštaurácie v Bratislave").
 *
 * Structure mirrors LocalSEOPage but adds:
 *  - Category-aware H1/H2 (from API)
 *  - Internal cross-links to OTHER categories in same city (SEO linking gold)
 *  - Internal cross-links to SAME category in other cities (geo-grid)
 *  - JSON-LD LocalBusiness schema for category-specific rich results
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  TrendingUp, Search, Eye, Mic, Globe, Shield, CheckCircle,
  MapPin, Zap, ChevronRight, Utensils, Scissors, Stethoscope,
  Dumbbell, Wrench, Scale, Flower, Glasses, Hotel, Coffee, Grid3x3,
} from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";

const CATEGORY_ICONS = {
  restauracie: Utensils, kadernictva: Scissors, lekari: Stethoscope,
  fitness: Dumbbell, autoservis: Wrench, advokati: Scale,
  kvetinarstva: Flower, optici: Glasses, hotely: Hotel, kaviarne: Coffee,
};

const sluggify = (text) =>
  String(text || "").toLowerCase().replace(/ /g, "-").replace(
    /[ãöüäéèøåžšččřőűńłșțğı]/g,
    (m) => ({ ã:"a",ö:"o",ü:"u",ä:"a",é:"e",è:"e",ø:"o",å:"a",
              ž:"z",š:"s",č:"c",ř:"r",ő:"o",ű:"u",ń:"n",ł:"l",
              ș:"s",ț:"t",ğ:"g",ı:"i" }[m] || m)
  );

export default function LocalSEOBusinessPage() {
  const { lang, city, category } = useParams();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    axios
      .get(`${API}/seo/local/${lang}/${city}/${category}`)
      .then((r) => {
        if (r.data?.error) setErr(r.data.error);
        else setData(r.data);
      })
      .catch((e) => setErr(String(e?.message || e)));
  }, [lang, city, category]);

  if (err) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] text-white flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Page not found</h1>
          <p className="text-[var(--color-text-muted)] mb-4">{err}</p>
          <Link to="/" className="text-[#a855f7] underline">Back to home</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#a855f7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const CategoryIcon = CATEGORY_ICONS[data.category] || Grid3x3;
  const canonicalUrl = `https://toprankpulse.com/seo/${lang}/${city}/${category}`;

  // JSON-LD for rich snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": data.h1,
    "description": data.desc,
    "url": canonicalUrl,
    "areaServed": { "@type": "City", "name": data.city, "addressCountry": data.flag },
    "provider": {
      "@type": "Organization", "name": "TopRankPulse",
      "url": "https://toprankpulse.com",
    },
    "audience": { "@type": "Audience", "audienceType": data.category_label },
  };

  // Additional FAQPage schema if we have enriched FAQ — major rich-snippet booster
  const faqJsonLd = (data.enriched_content?.faq && data.enriched_content.faq.length > 0) ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.enriched_content.faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white" data-testid="local-business-page">
      <Helmet>
        <title>{`${data.h1} | TopRankPulse`}</title>
        <meta name="description" content={data.desc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={data.h1} />
        <meta property="og:description" content={data.desc} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        {faqJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        )}
      </Helmet>

      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="hidden sm:flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-white font-['JetBrains_Mono'] uppercase">Home</a>
            <a href={`/seo/${lang}`} className="text-sm text-[var(--color-text-muted)] hover:text-white font-['JetBrains_Mono'] uppercase">{data.country}</a>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] font-['JetBrains_Mono'] flex-wrap">
          <a href="/" className="hover:text-white">Home</a>
          <ChevronRight className="w-3 h-3" />
          <a href={`/seo/${lang}`} className="hover:text-white">{data.country}</a>
          <ChevronRight className="w-3 h-3" />
          <a href={`/seo/${lang}/${data.city_slug}`} className="hover:text-white">{data.city}</a>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white">{data.category_label}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <CategoryIcon className="w-5 h-5 text-[#a855f7]" />
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase">
              {data.city} / {data.category_label}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6" data-testid="business-h1">
            {data.h1}
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] mb-4 max-w-2xl mx-auto">{data.h2}</p>
          <p className="text-sm text-[var(--color-text-muted)] mb-10 max-w-2xl mx-auto">{data.desc}</p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-4">
            <input
              data-testid="business-url-input"
              type="text" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder={`${data.category_label} URL...`}
              className="flex-1 px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]"
            />
            <a
              href={url ? `/?url=${encodeURIComponent(url)}` : "/"}
              className="px-8 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all flex items-center justify-center gap-3"
              data-testid="business-scan-btn"
            >
              <Search className="w-5 h-5" />SCAN FREE
            </a>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">
            No credit card · Instant results · 26 languages
          </p>
        </div>
      </section>

      {/* Why care */}
      <section className="py-16 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">
            {t.lsb_why || "Why does it matter for your"} <span className="text-[#a855f7]">{data.category_plural}</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Eye, val: "73%",
                titleKey: "lsb_pt1", titleFb: "Sites invisible to AI",
                descKey: "lsb_pd1",  descFb: `${data.category_plural} stránky, ktoré nie sú citované AI vyhľadávačmi, strácajú väčšinu novej generácie zákazníkov.` },
              { icon: Globe, val: "+312%",
                titleKey: "lsb_pt2", titleFb: "Local search growth",
                descKey: "lsb_pd2",  descFb: `Lokálne vyhľadávania "${data.category_plural} ${data.city}" rastú rok čo rok 312% pre AI-driven traffic.` },
              { icon: Zap, val: "30s",
                titleKey: "lsb_pt3", titleFb: "Quick free audit",
                descKey: "lsb_pd3",  descFb: `Skontrolujte vašu lokálnu viditeľnosť za pol minúty — bez registrácie.` },
            ].map((s, i) => (
              <div key={i} className="bg-[var(--color-bg)] border border-[var(--color-border)] p-6">
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-[#a855f7]">{s.val}</p>
                <s.icon className="w-5 h-5 text-[var(--color-text-muted)] mt-3 mb-2" />
                <h3 className="font-bold text-white font-['Cabinet_Grotesk'] mb-1">{t[s.titleKey] || s.titleFb}</h3>
                <p className="text-sm text-[var(--color-text-muted)]">{t[s.descKey] || s.descFb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* V165.4 — LLM-enriched unique content (800-1500 SK words per combo) */}
      {data.enriched_content && (
        <section className="py-16 px-6 border-y border-[var(--color-border)]" data-testid="enriched-content">
          <div className="max-w-3xl mx-auto">
            {/* Intro paragraph */}
            {data.enriched_content.intro && (
              <article className="prose prose-invert max-w-none mb-12">
                <p className="text-base text-[var(--color-text-muted)] leading-relaxed whitespace-pre-line">
                  {data.enriched_content.intro}
                </p>
              </article>
            )}

            {/* AI visibility analysis */}
            {data.enriched_content.ai_visibility && (
              <div className="mb-12">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
                  {t.lsb_ai_h || "AI vyhľadávanie a"} <span className="text-[#a855f7]">{data.category_label}</span>
                </h2>
                <p className="text-[var(--color-text-muted)] leading-relaxed whitespace-pre-line">
                  {data.enriched_content.ai_visibility}
                </p>
              </div>
            )}

            {/* Top 3 issues */}
            {Array.isArray(data.enriched_content.top_3_issues) && data.enriched_content.top_3_issues.length > 0 && (
              <div className="mb-12">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-6">
                  {t.lsb_issues_h || "3 najväčšie SEO problémy pre"} <span className="text-[#a855f7]">{data.category_plural}</span>
                </h2>
                <div className="space-y-4">
                  {data.enriched_content.top_3_issues.slice(0, 3).map((iss, i) => (
                    <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5">
                      <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                        <span className="text-[#a855f7] text-sm font-['JetBrains_Mono']">0{i + 1}</span>
                        {iss.title}
                      </h3>
                      <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
                        {iss.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick wins */}
            {data.enriched_content.quick_wins && (
              <div className="mb-12 bg-[var(--color-surface)] border border-[#a855f7]/40 p-6">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#a855f7]" />
                  {t.lsb_qwins_h || "Quick wins — tento týždeň"}
                </h2>
                <p className="text-[var(--color-text-muted)] leading-relaxed whitespace-pre-line">
                  {data.enriched_content.quick_wins}
                </p>
              </div>
            )}

            {/* Local competition */}
            {data.enriched_content.local_competition && (
              <div className="mb-12">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
                  {t.lsb_comp_h || "Lokálna konkurencia v"} {data.city}
                </h2>
                <p className="text-[var(--color-text-muted)] leading-relaxed whitespace-pre-line">
                  {data.enriched_content.local_competition}
                </p>
              </div>
            )}

            {/* FAQ */}
            {Array.isArray(data.enriched_content.faq) && data.enriched_content.faq.length > 0 && (
              <div data-testid="enriched-faq">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-6">
                  {t.lsb_faq_h || "Časté otázky"}
                </h2>
                <div className="space-y-3">
                  {data.enriched_content.faq.map((f, i) => (
                    <details key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 group">
                      <summary className="font-bold text-white cursor-pointer flex items-center justify-between gap-2 list-none">
                        <span>{f.q}</span>
                        <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)] group-open:rotate-90 transition-transform" />
                      </summary>
                      <p className="mt-3 text-sm text-[var(--color-text-muted)] leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related categories in same city — CRITICAL for SEO interlinking */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-8">
            {t.lsb_other_cats || "Other businesses in"} <span className="text-[#a855f7]">{data.city}</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="related-categories">
            {data.all_categories.filter(c => c.key !== data.category).slice(0, 10).map((c) => {
              const Icon = CATEGORY_ICONS[c.key] || Grid3x3;
              return (
                <Link
                  key={c.key}
                  to={`/seo/${lang}/${data.city_slug}/${c.key}`}
                  className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 hover:border-[#a855f7] transition-colors group flex flex-col items-center text-center"
                  data-testid={`related-cat-${c.key}`}
                >
                  <Icon className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-[#a855f7] mb-2 transition-colors" />
                  <span className="text-sm font-bold text-white">{c.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Same category in other cities — GEO interlinking */}
      {data.cities && data.cities.length > 0 && (
        <section className="py-16 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-8">
              <span className="text-[#a855f7]">{data.category_label}</span> {t.lsb_other_cities || "in other cities"}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="other-cities">
              {data.cities.filter(c => sluggify(c) !== city.toLowerCase()).slice(0, 10).map((c) => (
                <Link
                  key={c}
                  to={`/seo/${lang}/${sluggify(c)}/${data.category}`}
                  className="bg-[var(--color-bg)] border border-[var(--color-border)] p-4 hover:border-[#a855f7] transition-colors group flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4 text-[var(--color-text-muted)] group-hover:text-[#a855f7] transition-colors" />
                  <span className="text-sm font-bold text-white truncate">{c}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.lsb_cta_h1 || "START YOUR FREE"} <span className="text-[#a855f7]">SEO AUDIT</span>
          </h2>
          <p className="text-[var(--color-text-muted)] mb-8">
            {t.lsb_cta_join || "Join"} {data.category_plural} {t.lsb_cta_in || "in"} {data.city} {t.lsb_cta_using || "using TopRankPulse"}.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 transition-all"
            data-testid="business-cta"
          >
            <Search className="w-6 h-6" />{t.lsb_scan || "SCAN YOUR WEBSITE"}
          </a>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-[var(--color-text-muted)] text-sm">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" />{t.lsb_free || "Free forever"}</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" />{t.lsb_no_card || "No credit card"}</span>
            <span className="flex items-center gap-2"><Mic className="w-4 h-4" />{t.lsb_voice || "Voice search ready"}</span>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
